import * as XLSX from 'xlsx';

export interface ParsedFileData {
  fileName: string;
  fileType: 'csv' | 'xlsx' | 'json';
  headers: string[];
  rows: Record<string, string>[];
  rowCount: number;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_ROWS = 500;

function parseCSVLine(line: string): string[] {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ',' || char === ';') {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
  }
  values.push(current.trim());
  return values;
}

function parseCSV(text: string): { headers: string[]; rows: Record<string, string>[] } {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length === 0) throw new Error('CSV file is empty');

  const headers = parseCSVLine(lines[0]);
  const rows: Record<string, string>[] = [];

  for (let i = 1; i < Math.min(lines.length, MAX_ROWS + 1); i++) {
    const values = parseCSVLine(lines[i]);
    const row: Record<string, string> = {};
    headers.forEach((h, idx) => {
      row[h] = values[idx] || '';
    });
    rows.push(row);
  }

  return { headers, rows };
}

function parseXLSX(buffer: ArrayBuffer): { headers: string[]; rows: Record<string, string>[] } {
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) throw new Error('No sheets found in file');

  const sheet = workbook.Sheets[sheetName];
  const jsonData = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });

  if (jsonData.length === 0) throw new Error('Spreadsheet is empty');

  const headers = Object.keys(jsonData[0]);
  const rows = jsonData.slice(0, MAX_ROWS).map(row => {
    const stringRow: Record<string, string> = {};
    headers.forEach(h => {
      stringRow[h] = String(row[h] ?? '');
    });
    return stringRow;
  });

  return { headers, rows };
}

function parseJSON(text: string): { headers: string[]; rows: Record<string, string>[] } {
  const data = JSON.parse(text);
  const array = Array.isArray(data) ? data : data.data || data.rows || data.items;

  if (!Array.isArray(array) || array.length === 0) {
    throw new Error('JSON must contain an array of objects');
  }

  const headers = Object.keys(array[0]);
  const rows = array.slice(0, MAX_ROWS).map((item: Record<string, unknown>) => {
    const row: Record<string, string> = {};
    headers.forEach(h => {
      row[h] = String(item[h] ?? '');
    });
    return row;
  });

  return { headers, rows };
}

function getFileType(fileName: string): ParsedFileData['fileType'] {
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (ext === 'csv') return 'csv';
  if (ext === 'xlsx' || ext === 'xls') return 'xlsx';
  if (ext === 'json') return 'json';
  throw new Error(`Unsupported file type: .${ext}. Use CSV, XLSX, or JSON.`);
}

export async function parseFile(file: File): Promise<ParsedFileData> {
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`File too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum is 5MB.`);
  }

  const fileType = getFileType(file.name);
  let headers: string[];
  let rows: Record<string, string>[];

  if (fileType === 'xlsx') {
    const buffer = await file.arrayBuffer();
    ({ headers, rows } = parseXLSX(buffer));
  } else {
    const text = await file.text();
    if (fileType === 'csv') {
      ({ headers, rows } = parseCSV(text));
    } else {
      ({ headers, rows } = parseJSON(text));
    }
  }

  return {
    fileName: file.name,
    fileType,
    headers,
    rows,
    rowCount: rows.length,
  };
}
